import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './filter-bar.lit.scss';
import getString from './strings';

export enum KatFilterBarAction {
  APPLY = 'apply',
  CLEAR = 'clear',
  FILTERS = 'filters',
  SEARCH = 'search',
  SETTINGS = 'settings',
  SORT = 'sort',
}

/**
 * @component {kat-filter-bar} KatFilterBar The filter bar provides a standard UI for controlling the content of lists of items, including tables and grids.
 * @status Production
 * @theme flo
 * @example All {"show-action-search": true,"show-action-apply": true, "show-action-clear": true, "show-action-filters": true, "show-action-settings": true, "show-action-sort": true}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-filter-bar')
export class KatFilterBar extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * @classprop {enum} KatFilterBarAction @hidden
   * @enum {value} apply Apply action.
   * @enum {value} clear Clear action.
   * @enum {value} filters Filters action.
   * @enum {value} search Search action.
   * @enum {value} settings Settings action.
   * @private
   */

  /** Enables the search button */
  @property({ attribute: 'show-action-search', reflect: true })
  showActionSearch = false;

  /** Enables the Apply action */
  @property({ attribute: 'show-action-apply', reflect: true })
  showActionApply = false;

  /** Enables the Clear action */
  @property({ attribute: 'show-action-clear', reflect: true })
  showActionClear = false;

  /** Enables the Filters action */
  @property({ attribute: 'show-action-filters', reflect: true })
  showActionFilters = false;

  /** Enables the Settings action */
  @property({ attribute: 'show-action-settings', reflect: true })
  showActionSettings = false;

  /** Enables the Sort action */
  @property({ attribute: 'show-action-sort', reflect: true })
  showActionSort = false;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property({ reflect: true })
  locale?: string;

  /**
   * Triggers when an action is clicked.
   */
  @event('action', true)
  private _actionEvent: EventEmitter<{ name: KatFilterBarAction }>;

  private _handleSearchClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.SEARCH });
  }

  private _handleApplyClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.APPLY });
  }

  private _handleClearClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.CLEAR });
  }

  private _handleFiltersClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.FILTERS });
  }

  private _handleSettingsClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.SETTINGS });
  }

  private _handleSortClick() {
    this._actionEvent.emit({ name: KatFilterBarAction.SORT });
  }

  private _getRenderedSearch() {
    if (!this.showActionSearch) {
      return nothing;
    }

    const text = getString('kat-filter-bar-search', undefined, this.locale);

    return html`<kat-button
      id="search"
      variant="secondary"
      @click=${this._handleSearchClick}
      ><span
        ><span class="hidden-below-xxl">${text}</span
        ><kat-icon
          class="visible-below-xxl"
          name="search"
          size="small"
        ></kat-icon></span
    ></kat-button>`;
  }

  private _getRenderedClear() {
    if (!this.showActionClear) {
      return nothing;
    }

    const text = getString('kat-filter-bar-clear', undefined, this.locale);

    return html`<kat-button
      id="clear"
      variant="utility-link"
      @click=${this._handleClearClick}
      ><span
        ><span class="hidden-below-xxl">${text}</span
        ><kat-icon
          class="visible-below-xxl"
          name="clear"
          size="small"
        ></kat-icon></span
    ></kat-button>`;
  }

  private _getRenderedApply() {
    if (!this.showActionApply) {
      return nothing;
    }

    const text = getString('kat-filter-bar-apply', undefined, this.locale);

    return html`<kat-button
      id="apply"
      variant="secondary"
      @click=${this._handleApplyClick}
      ><span
        ><span class="hidden-below-xxl">${text}</span
        ><kat-icon
          class="visible-below-xxl"
          name="check"
          size="small"
        ></kat-icon></span
    ></kat-button>`;
  }

  private _getRenderedFilters() {
    if (!this.showActionFilters) {
      return nothing;
    }

    return html`<kat-button
      id="filters"
      variant="utility-link"
      @click=${this._handleFiltersClick}
      ><kat-icon name="all_filters" size="small"></kat-icon>
    </kat-button>`;
  }

  private _getRenderedSettings() {
    if (!this.showActionSettings) {
      return nothing;
    }

    return html`<kat-button
      id="settings"
      variant="utility-link"
      @click=${this._handleSettingsClick}
    >
      <kat-icon name="settings" size="small"></kat-icon>
    </kat-button>`;
  }

  private _getRenderedSort() {
    if (!this.showActionSort) {
      return nothing;
    }

    return html`<kat-button
      id="sort"
      variant="utility-link"
      @click=${this._handleSortClick}
    >
      <kat-icon name="sort" size="small"></kat-icon>
    </kat-button>`;
  }

  render() {
    return html`<div class="search">
        <slot name="search"></slot>
        ${this._getRenderedSearch()}
      </div>
      <div class="filters">
        <slot></slot>
      </div>
      <div class="actions">
        ${this._getRenderedClear()} ${this._getRenderedApply()}
        ${this._getRenderedSort()} ${this._getRenderedFilters()}
        ${this._getRenderedSettings()}
      </div>`;
  }
}
